import { SocialNetworksId } from "@/interfaces";
import { PostStatsBreakdownEntry } from "@/services/bedrock/dtos/Stats";

export type PerfMetric = "impressions" | "reactions" | "comments";

export interface PostAggregate {
  postId: number;
  playerId: number;
  playerName: string;
  thumbnailUrl: string | null;
  postedAt: string | null;
  networks: SocialNetworksId[];
  primaryNetwork: SocialNetworksId;
  externalPostUrl: string | null;
  impressions: number;
  reactions: number;
  comments: number;
  shares: number;
}

type Counts = Pick<PostAggregate, "impressions" | "reactions" | "comments" | "shares">;

export function liveEntries(breakdown: PostStatsBreakdownEntry[]): PostStatsBreakdownEntry[] {
  return breakdown.filter(entry => entry.status === "POSTED");
}

function timeOf(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

/** Ascending by posted time, undated entries last. */
function byPostedAsc(a: PostStatsBreakdownEntry, b: PostStatsBreakdownEntry): number {
  const ta = timeOf(a.postedTimestamp);
  const tb = timeOf(b.postedTimestamp);
  if (ta == null && tb == null) return 0;
  if (ta == null) return 1;
  if (tb == null) return -1;
  return ta - tb;
}

/** The same fields bedrock totals and the All posts table shows, so a card's counts add up to them. */
function entryCounts(entry: PostStatsBreakdownEntry): Counts {
  const stats = entry.latestStats;
  return {
    impressions: stats?.impressionCount ?? 0,
    reactions: stats?.totalReactions ?? 0,
    comments: stats?.commentCount ?? 0,
    shares: stats?.shareCount ?? 0,
  };
}

/** One aggregate per post, summing its live network entries; the earliest-posted network leads. */
export function groupByPost(breakdown: PostStatsBreakdownEntry[]): PostAggregate[] {
  const byPost = new Map<number, PostStatsBreakdownEntry[]>();
  liveEntries(breakdown).forEach(entry => {
    // Rows without a post id can't be grouped or opened.
    if (entry.postId == null) return;
    const entries = byPost.get(entry.postId) ?? [];
    entries.push(entry);
    byPost.set(entry.postId, entries);
  });

  return Array.from(byPost.entries()).map(([postId, entries]) => {
    const ordered = [...entries].sort(byPostedAsc);
    const first = ordered[0];
    const totals = ordered.reduce<Counts>(
      (sum, entry) => {
        const counts = entryCounts(entry);
        return {
          impressions: sum.impressions + counts.impressions,
          reactions: sum.reactions + counts.reactions,
          comments: sum.comments + counts.comments,
          shares: sum.shares + counts.shares,
        };
      },
      { impressions: 0, reactions: 0, comments: 0, shares: 0 }
    );
    return {
      postId,
      playerId: first.playerId,
      playerName: first.playerName,
      thumbnailUrl: ordered.find(entry => entry.thumbnailUrl)?.thumbnailUrl ?? null,
      postedAt: first.postedTimestamp || null,
      networks: ordered.map(entry => entry.socialNetworkId),
      primaryNetwork: first.socialNetworkId,
      externalPostUrl: first.externalPostUrl || null,
      ...totals,
    };
  });
}

export interface UsageSummary {
  /** Posts made from the design with at least one network live. */
  uses: number;
  /** Live network posts across those uses. */
  channelPosts: number;
  /** Live network posts per network, most first. */
  byNetwork: { network: SocialNetworksId; count: number }[];
  players: number;
}

export function usageSummary(breakdown: PostStatsBreakdownEntry[]): UsageSummary {
  const live = liveEntries(breakdown);
  const byNetwork = new Map<SocialNetworksId, number>();
  live.forEach(entry => byNetwork.set(entry.socialNetworkId, (byNetwork.get(entry.socialNetworkId) ?? 0) + 1));
  return {
    uses: new Set(live.filter(entry => entry.postId != null).map(entry => entry.postId)).size,
    channelPosts: live.length,
    byNetwork: Array.from(byNetwork, ([network, count]) => ({ network, count })).sort((a, b) => b.count - a.count),
    players: new Set(live.map(entry => entry.playerId)).size,
  };
}

/** Newest first, undated posts last. */
function byPostedDesc(a: PostAggregate, b: PostAggregate): number {
  const ta = timeOf(a.postedAt);
  const tb = timeOf(b.postedAt);
  if (ta == null && tb == null) return 0;
  if (ta == null) return 1;
  if (tb == null) return -1;
  return tb - ta;
}

export function sortRecent(aggregates: PostAggregate[]): PostAggregate[] {
  return [...aggregates].sort(byPostedDesc);
}

export function sortTop(aggregates: PostAggregate[], metric: PerfMetric): PostAggregate[] {
  return [...aggregates].sort((a, b) => b[metric] - a[metric] || byPostedDesc(a, b));
}

export { formatCount } from "@/components/PostStatsRow";
